import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, Users, CreditCard, TrendingUp, ShieldAlert, AlertTriangle } from "lucide-react";

const RISK_LABEL = {
  trial_expiring: "Teste acabando",
  past_due: "Pagamento pendente",
  expired: "Teste expirado",
};
const RISK_TONE = {
  trial_expiring: "text-amber-700 bg-amber-100",
  past_due: "text-orange-700 bg-orange-100",
  expired: "text-red-700 bg-red-100",
};

const STATUS_TONE = {
  ACTIVE: "text-emerald-700 bg-emerald-100",
  TRIALING: "text-sky-700 bg-sky-100",
  PAST_DUE: "text-orange-700 bg-orange-100",
  EXPIRED: "text-red-700 bg-red-100",
  CANCELLED: "text-slate-700 bg-slate-200",
  SUSPENDED: "text-red-700 bg-red-100",
};

const STATUS_LABEL = {
  ACTIVE: "Ativa", TRIALING: "Em teste", PAST_DUE: "Pagamento pendente",
  EXPIRED: "Expirada", CANCELLED: "Cancelada", SUSPENDED: "Suspensa",
};

function brl(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function Metric({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`metric-${label}`}>
      <div className="flex items-center gap-2 text-muted-foreground text-sm"><Icon className="h-4 w-4" />{label}</div>
      <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function Platform() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_platform_admin) return;
    (async () => {
      try {
        const [o, t] = await Promise.all([api.get("/platform/overview"), api.get("/platform/tenants")]);
        setOverview(o.data);
        setTenants(t.data || []);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (user && !user.is_platform_admin) return <Navigate to="/" replace />;

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; } };

  return (
    <div className="space-y-6" data-testid="platform-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
          <ShieldAlert className="h-7 w-7 text-accent" /> Admin da Plataforma
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Visão geral de todas as organizações, assinaturas e uso do TRIA.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : (
        <>
          {overview?.at_risk > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="risk-alert">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>{overview.at_risk} organização(ões) em risco.</strong> Há clientes com teste acabando (≤3 dias), pagamento pendente ou teste expirado. Entre em contato antes de perdê-los — veja os destaques na tabela abaixo.
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Metric icon={Building2} label="Organizações" value={overview?.total_orgs ?? 0} />
            <Metric icon={Users} label="Usuários" value={overview?.total_users ?? 0} />
            <Metric icon={TrendingUp} label="MRR estimado" value={brl(overview?.mrr)} hint="Assinaturas ativas" />
            <Metric icon={CreditCard} label="Assinaturas ativas" value={overview?.subscriptions_by_status?.ACTIVE ?? 0} />
            <Metric icon={AlertTriangle} label="Em risco" value={overview?.at_risk ?? 0} hint="Teste/pagamento" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="font-semibold mb-3">Por status</h2>
              <div className="space-y-2">
                {Object.entries(overview?.subscriptions_by_status || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TONE[k] || "bg-slate-100 text-slate-700"}`}>{STATUS_LABEL[k] || k}</span>
                    <span className="font-num">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <h2 className="font-semibold mb-3">Por plano</h2>
              <div className="space-y-2">
                {Object.entries(overview?.subscriptions_by_plan || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{k}</span>
                    <span className="font-num">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border overflow-hidden">
            <div className="px-5 py-3 border-b border-border font-semibold">Organizações ({tenants.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-4 py-3">Organização</th>
                    <th className="text-left font-medium px-4 py-3">Proprietário</th>
                    <th className="text-left font-medium px-4 py-3">Plano</th>
                    <th className="text-left font-medium px-4 py-3">Status</th>
                    <th className="text-right font-medium px-4 py-3">Usuários</th>
                    <th className="text-right font-medium px-4 py-3">Lotes</th>
                    <th className="text-right font-medium px-4 py-3">Mercad.</th>
                    <th className="text-right font-medium px-4 py-3">Vendas/mês</th>
                    <th className="text-left font-medium px-4 py-3">Criada em</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Nenhuma organização.</td></tr>
                  ) : tenants.map((t) => (
                    <tr key={t.id} className={`border-t border-border hover:bg-secondary/30 ${t.risk ? "bg-amber-50/60" : ""}`} data-testid="tenant-row">
                      <td className="px-4 py-3 font-medium">{t.name}</td>
                      <td className="px-4 py-3">
                        <div>{t.owner_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{t.owner_email || ""}</div>
                      </td>
                      <td className="px-4 py-3">{t.plan}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TONE[t.status] || "bg-slate-100 text-slate-700"}`}>{STATUS_LABEL[t.status] || t.status}</span>
                        {t.risk && (
                          <span className={`ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${RISK_TONE[t.risk]}`} data-testid={`risk-badge-${t.id}`}>
                            <AlertTriangle className="h-3 w-3" />
                            {RISK_LABEL[t.risk]}{t.risk === "trial_expiring" && t.trial_days_left != null ? ` (${t.trial_days_left}d)` : ""}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-num">{t.users}</td>
                      <td className="px-4 py-3 text-right font-num">{t.lots}</td>
                      <td className="px-4 py-3 text-right font-num">{t.items}</td>
                      <td className="px-4 py-3 text-right font-num">{t.monthly_sales}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
